import type { Availability, ChatStatus, MemberPresence, Presence } from "@beacon/core/chat"
import { queryOptions, type QueryKey } from "@tanstack/react-query"
import { call } from "@/administration/client.ts"

/*
 * Presence and status in the web app: what it reads of how colleagues are seen (online or away,
 * and their status), the Member's own availability and how they set it, and whether they are idle,
 * which their client tells Chat's live socket. Presence is read again on each `presence` hint and
 * every minute regardless, since a colleague whose client vanished goes away with no hint.
 */

/** How long without the Member using the page before their client says they are idle. */
export const idleAfter = 10 * 60_000

/** How often presence is read again while on screen, hint or no hint. */
export const presenceReadEvery = 60_000

/** Presence's place in the query cache. */
export const presenceKeys = {
  all: ["chat", "presence"] as const,
  availability: ["chat", "availability"] as const,
  /** The read for some Members, the same whatever order they are asked in. */
  of: (ids: readonly string[]) => ["chat", "presence", ...[...new Set(ids)].sort()] as const,
}

/** Whether a cached query is a presence read that includes `memberId`, for a hint about them. */
export function touchesMember(queryKey: QueryKey, memberId: string) {
  return queryKey[0] === "chat" && queryKey[1] === "presence" && queryKey.includes(memberId)
}

/** The Member's own availability: their status, appearing offline, and how others see them. */
export const availabilityQuery = queryOptions({
  queryKey: presenceKeys.availability,
  queryFn: () => call<Availability>("GET", "/chat/availability"),
  refetchInterval: presenceReadEvery,
})

/** How these Members are seen now, by id. */
export const presenceQuery = (ids: readonly string[]) =>
  queryOptions({
    queryKey: presenceKeys.of(ids),
    queryFn: async () => {
      const asked = [...new Set(ids)].sort()
      const { presence } = await call<{ presence: MemberPresence[] }>(
        "GET",
        `/chat/presence?${new URLSearchParams({ ids: asked.join(",") }).toString()}`,
      )
      return new Map(presence.map((one) => [one.memberId, one]))
    },
    enabled: ids.length > 0,
    staleTime: presenceReadEvery / 2,
    refetchInterval: presenceReadEvery,
  })

export const setStatus = (status: ChatStatus) =>
  call<Availability>("PUT", "/chat/availability/status", status)

export const clearStatus = () => call<Availability>("DELETE", "/chat/availability/status")

export const setAppearOffline = (appearOffline: boolean) =>
  call<Availability>("PUT", "/chat/availability/appear-offline", { appearOffline })

/**
 * Whether the Member is idle: `used` on each sign they are using the page, `check` now and then,
 * `onIdle` told each time it changes. They are idle once they haven't used it for `idleAfter`.
 */
export function activityWatch({
  now = () => Date.now(),
  onIdle,
}: {
  now?: () => number
  onIdle(idle: boolean): void
}) {
  let usedAt = now()
  let idle = false
  return {
    idle: () => idle,
    used() {
      usedAt = now()
      if (!idle) return
      idle = false
      onIdle(false)
    },
    check() {
      if (idle || now() - usedAt < idleAfter) return
      idle = true
      onIdle(true)
    },
  }
}

/** When a status clears, as the Member chooses it. */
export type ClearAfter = "never" | "30m" | "1h" | "4h" | "today" | "week"

export const clearAfterChoices: readonly { value: ClearAfter; label: string }[] = [
  { value: "never", label: "Don't clear" },
  { value: "30m", label: "30 minutes" },
  { value: "1h", label: "1 hour" },
  { value: "4h", label: "4 hours" },
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
]

const minutes = { "30m": 30, "1h": 60, "4h": 240 } as const

/** The instant a status chosen at `now` clears, by the Member's own clock; none for `never`. */
export function untilOf(choice: ClearAfter, now: number): string | null {
  if (choice === "never") return null
  if (choice in minutes)
    return new Date(now + minutes[choice as keyof typeof minutes] * 60_000).toISOString()
  const day = new Date(now)
  // Midnight after today, or after this week's Sunday.
  const days = choice === "today" ? 1 : ((7 - day.getDay()) % 7) + 1
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + days).toISOString()
}

/** The calendar day (as days since 1970) and the time of day `at` is, on the clock of `zone`. */
function clockOf(at: Date, zone: string) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(at)
      .map(({ type, value }) => [type, Number(value)]),
  )
  return {
    day: Date.UTC(parts.year!, parts.month! - 1, parts.day!) / 86_400_000,
    hour: parts.hour!,
    minute: parts.minute!,
  }
}

/** Which day `at` is, relative to `now`'s, in `zone`: "today", "tomorrow", or its name and date. */
function dayName(at: Date, now: Date, zone: string) {
  const apart = clockOf(at, zone).day - clockOf(now, zone).day
  if (apart === 0) return "today"
  if (apart === 1) return "tomorrow"
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: zone,
  }).format(at)
}

/**
 * When a status clears, said as its Member's colleagues read it: on the reader's clock (`zone`,
 * as `useViewerZone` gives it), as their messages' times are. Empty when it never does.
 */
export function describeUntil(until: string | null, now: number, zone: string): string {
  if (!until) return ""
  const at = new Date(until)
  const today = new Date(now)
  const clock = clockOf(at, zone)
  // Midnight is the end of the day before it.
  if (clock.hour === 0 && clock.minute === 0)
    return `Until the end of ${dayName(new Date(at.getTime() - 60_000), today, zone)}`
  const day = dayName(at, today, zone)
  const time = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: zone,
  }).format(at)
  if (day === "today") return `Until ${time}`
  return `Until ${day === "tomorrow" ? "tomorrow" : day}, ${time}`
}

/** A status as it shows at `now`: none once the time it clears at has passed. */
export function statusShown(status: ChatStatus | null, now: number) {
  if (!status) return null
  return status.until && new Date(status.until).getTime() <= now ? null : status
}

/** A status as one line: its emoji, then its words. */
export const describeStatus = (status: ChatStatus) =>
  [status.emoji, status.text].filter(Boolean).join(" ")

/** Presence as a word; the Member's own away says it is them appearing offline, when it is. */
export function describePresence(presence: Presence, own?: { appearOffline: boolean }) {
  if (own?.appearOffline) return "Appearing offline"
  return presence === "online" ? "Online" : "Away"
}
